import { Clock, Package, Truck, Activity } from 'lucide-react';
import type { TimelineBlock, TimelineEvent } from '../../../types/investigator';
import { PROVENANCE_DOT_CLASSES } from '../../../config/colors';
import { Badge, type BadgeTone } from '../../ui/badge';
import { formatUkDate, formatUkTime } from '../../../lib/calc';

// Tier colours come from the shared colors module: an inference
// is NOT a confirmation — inferred is violet, never the green of observed
// (§13.2 keeps provenance a distinct axis; §11 requires the difference
// between seen and deduced to stay visible). Dots keep the module's dot
// map; `estimated` and `unknown` are claim-level values beyond the three
// provenance tiers, so their badge colours take the nearest tone.
const PROVENANCE_DOT: Record<string, string> = {
  ...PROVENANCE_DOT_CLASSES,
  estimated: 'bg-amber-400 border-amber-600',
  unknown: 'bg-muted-foreground border-accent',
};

const PROVENANCE_BADGE: Record<string, { tone: BadgeTone; provenance?: 'observed' | 'declared' | 'inferred' }> = {
  observed:  { tone: 'neutral', provenance: 'observed' },
  declared:  { tone: 'neutral', provenance: 'declared' },
  inferred:  { tone: 'neutral', provenance: 'inferred' },
  estimated: { tone: 'warning' },
  unknown:   { tone: 'neutral' },
};

const PROVENANCE_LABEL: Record<string, string> = {
  observed: 'Confirmed',
  declared: 'Declared',
  inferred: 'Inferred',
  estimated: 'Estimated',
  unknown: 'Unknown',
};

function eventIcon(label: string) {
  const l = label.toLowerCase();
  if (l.includes('order') || l.includes('creat')) return Package;
  if (l.includes('pick') || l.includes('load') || l.includes('truck')) return Truck;
  if (l.includes('anomal')) return Activity;
  return Clock;
}

function formatTime(iso: string): string {
  return formatUkTime(iso, {}, iso);
}

function formatDate(iso: string): string {
  return formatUkDate(iso, '');
}

interface EnrichedTimelineProps {
  block: TimelineBlock;
}

export default function EnrichedTimeline({ block }: EnrichedTimelineProps) {
  if (block.events.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm py-8">
        No timeline events
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto pr-1">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Timeline</h3>
      <div className="relative">
        {/* Vertical spine */}
        <div className="absolute left-3 top-2 bottom-2 w-px bg-border" />

        <ul className="space-y-1">
          {block.events.map((event: TimelineEvent, i: number) => {
            const Icon = eventIcon(event.label);
            const dotClass = PROVENANCE_DOT[event.provenance] ?? PROVENANCE_DOT.unknown;
            const badge = PROVENANCE_BADGE[event.provenance] ?? PROVENANCE_BADGE.unknown;

            return (
              <li key={`${event.claim_id}-${i}`}>
                {/* Gap bar between events */}
                {event.gap_label && (
                  <div className="relative pl-8 py-0.5">
                    <div className="text-xs text-muted-foreground font-mono flex items-center gap-1">
                      <span className="inline-block w-3 h-px bg-border" />
                      {event.gap_label}
                    </div>
                  </div>
                )}

                <div className="relative pl-8">
                  {/* Timeline dot */}
                  <div
                    className={`absolute left-1.5 top-3 w-3 h-3 rounded-full border-2 ${dotClass}`}
                  />

                  <div className="bg-card/60 rounded-lg p-3 border border-border/50">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-xs font-medium text-foreground">{event.label}</span>
                        <Badge {...badge}>
                          {PROVENANCE_LABEL[event.provenance] ?? event.provenance}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                        {formatDate(event.occurred_at)} {formatTime(event.occurred_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
